import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import ReminderFormModal from '@/components/family/ReminderFormModal';
import ReminderCalendar from '@/components/family/ReminderCalendar';
import axiosClient from '../../api/axiosClient';
import { uploadSeniorPhoto } from '../../api/seniorPhotoApi';
import SeniorAvatar from '../../components/SeniorAvatar';
import { normalizeDeviceStatus, isPaired, isOnline as isDeviceOnline } from '../../utils/deviceStatus';

/**
 * Phase 15.2 - Redesigned Senior Detail Seite
 * Modern, clean design with Hayat Design System
 * Tabs: Überblick, Erinnerungen, Notfälle, Einstellungen
 */
function FamilySeniorDetail() {
  const { seniorId } = useParams();
  const { familyUser, household, getAuthHeader, isAdmin } = useFamilyAuth();
  const [senior, setSenior] = useState(null);
  const [device, setDevice] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [reminderError, setReminderError] = useState('');
  
  // Phase 14.4: New states
  const [reminderView, setReminderView] = useState('list');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedDateReminders, setSelectedDateReminders] = useState(null);
  
  // Phase 14.2: Settings State
  const [settingsLanguage, setSettingsLanguage] = useState('de');
  const [settingsReligion, setSettingsReligion] = useState('none');
  const [settingsIslamic, setSettingsIslamic] = useState({});
  const [settingsChristian, setSettingsChristian] = useState({});  // NEW: Christian settings
  const [settingsModules, setSettingsModules] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  
  // Photo upload state
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');
  
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (household && household.id) {
      loadSeniorData();
    }
  }, [seniorId, household]);

  // Live Status Updates: Poll device status every 30 seconds (same as FamilyDashboard)
  useEffect(() => {
    if (!household || !household.id || loading) return;
    
    const refreshDeviceStatus = async () => {
      try {
        const devicesRes = await axiosClient.get(
          `/api/households/${household.id}/devices`
        );
        const deviceData = devicesRes.data.find(d => d.senior_id === seniorId);
        setDevice(deviceData);
        console.log('[SeniorDetail] 🔄 Device status refreshed');
      } catch (error) {
        console.error('[SeniorDetail] Failed to refresh device status:', error);
        // Silent failure - don't show error to user
      }
    };
    
    // Poll every 30 seconds
    const pollInterval = setInterval(refreshDeviceStatus, 30000);
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [household, seniorId, loading]);

  const loadSeniorData = async () => {
    try {
      if (!household || !household.id) {
        console.error('Household not available');
        return;
      }

      // Senior Profile laden
      const seniorRes = await axiosClient.get(
        `/api/households/${household.id}/seniors`
      );
      const seniorData = seniorRes.data.find(s => s.id === seniorId);
      setSenior(seniorData);
      
      // Initialize settings from senior data
      if (seniorData) {
        setSettingsLanguage(seniorData.language || 'de');
        setSettingsReligion(seniorData.religion || 'none');
        setSettingsIslamic(seniorData.islamic_settings || {
          enable_prayer_times: true,
          enable_adhan: false,
          enable_quran_verses: true,
          enable_stories: true,
          calculation_method: 'MWL'
        });
        setSettingsChristian(seniorData.christian_settings || {
          enable_prayer_times: true,
          enable_bible_verses: true,
          enable_stories: true,
          denomination: 'catholic'  // catholic, protestant, orthodox
        });
        setSettingsModules(seniorData.modules_enabled || {
          reminders: true,
          calls: true,
          religion_module: false,
          emergency: true
        });
      }

      // Device laden
      const devicesRes = await axiosClient.get(
        `/api/households/${household.id}/devices`
      );
      const deviceData = devicesRes.data.find(d => d.senior_id === seniorId);
      setDevice(deviceData);

      // Reminders laden
      await loadReminders();

      // Notfälle laden
      const activityRes = await axiosClient.get(
        `/api/activity/${household.id}`
      );
      
      // Backend may return { activities: [...], count: ... } or just an array
      const activitiesData = Array.isArray(activityRes.data)
        ? activityRes.data
        : activityRes.data.activities || [];
      
      const emergencyData = activitiesData.filter(
        a => a.type === 'emergency' && a.senior_id === seniorId
      );
      setEmergencies(emergencyData);
    } catch (error) {
      console.error('Fehler beim Laden der Senior-Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReminders = async () => {
    setLoadingReminders(true);
    setReminderError('');
    try {
      // Phase 15.5: Use new Family API endpoint with axiosClient (no need for manual headers)
      const response = await axiosClient.get(
        `/api/family/seniors/${seniorId}/reminders`
      );
      setReminders(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Erinnerungen:', error);
      if (error.response?.status === 403) {
        setReminderError('Sie haben keine Berechtigung, diese Erinnerungen anzusehen.');
      } else {
        setReminderError('Fehler beim Laden der Erinnerungen.');
      }
    } finally {
      setLoadingReminders(false);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Möchten Sie diese Erinnerung wirklich löschen?')) {
      return;
    }

    try {
      // Phase 15.5: Use new Family API endpoint with axiosClient
      await axiosClient.delete(
        `/api/family/reminders/${reminderId}`
      );
      await loadReminders();
      // Success toast (optional, can add later)
    } catch (error) {
      console.error('Fehler beim Löschen der Erinnerung:', error);
      if (error.response?.status === 403) {
        alert('Sie haben keine Berechtigung, Erinnerungen zu löschen.');
      } else {
        alert('Fehler beim Löschen der Erinnerung.');
      }
    }
  };

  const handleReminderSaved = async () => {
    setShowReminderForm(false);
    setShowReminderModal(false);
    setEditingReminder(null);
    await loadReminders();
  };

  // Settings Functions
  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setSettingsError('');
    setSettingsSuccess('');

    try {
      if (!familyUser) {
        setSettingsError('Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.');
        return;
      }

      const payload = {
        language: settingsLanguage,
        religion: settingsReligion,
        location: senior.location || '',  // NEW: Include location
        islamic_settings: settingsIslamic,
        christian_settings: settingsChristian,  // NEW: Christian settings
        modules_enabled: settingsModules
      };

      const response = await axiosClient.put(
        `/api/family/seniors/${seniorId}`,
        payload
      );

      setSenior(response.data);
      setSettingsSuccess('Einstellungen erfolgreich gespeichert!');
      setTimeout(() => setSettingsSuccess(''), 3000);
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellungen:', error);
      
      if (error.response?.status === 403) {
        setSettingsError(
          'Sie haben keine Berechtigung, diese Einstellungen zu ändern. Nur Admins können Änderungen vornehmen.'
        );
      } else if (error.response?.status === 422) {
        setSettingsError(
          'Validierungsfehler: Bitte überprüfen Sie alle Pflichtfelder.'
        );
      } else {
        setSettingsError(
          error.response?.data?.detail || 
          'Fehler beim Speichern der Einstellungen. Bitte versuchen Sie es erneut.'
        );
      }
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleResetSettings = () => {
    if (senior) {
      setSettingsLanguage(senior.language || 'de');
      setSettingsReligion(senior.religion || 'none');
      setSettingsIslamic(senior.islamic_settings || {});
      setSettingsModules(senior.modules_enabled || {});
      setSettingsError('');
      setSettingsSuccess('');
    }
  };

  const startVideoCall = (isDropIn = false) => {
    if (!device) {
      alert('Kein Gerät für diesen Senior gefunden');
      return;
    }
    if (device.status !== 'online') {
      alert('Das Gerät ist offline');
      return;
    }
    navigate(`/family/seniors/${seniorId}/call${isDropIn ? '?dropin=true' : ''}`);
  };


  // Photo upload handler
  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset errors
    setPhotoUploadError('');

    try {
      setIsUploadingPhoto(true);
      
      // Upload to backend
      const result = await uploadSeniorPhoto(senior.id, file);
      
      // Update senior state with new photo URL
      setSenior((prev) => ({
        ...prev,
        photo_url: result.photo_url,
        image_url: result.photo_url, // Also update legacy field for consistency
      }));
      
      console.log('✅ Photo uploaded successfully:', result.photo_url);
      
    } catch (err) {
      console.error('❌ Photo upload failed:', err);
      setPhotoUploadError(err.message || 'Foto-Upload fehlgeschlagen');
      
      // Show error for 5 seconds
      setTimeout(() => setPhotoUploadError(''), 5000);
    } finally {
      setIsUploadingPhoto(false);
      // Reset file input
      event.target.value = '';
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050816]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#5B4B8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#CBD5F5] font-medium">Lade Senior-Details...</p>
        </div>
      </div>
    );
  }

  if (!senior) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050816]">
        <div className="text-center">
          <div className="text-7xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-[#F9FAFB]">Senior nicht gefunden</h2>
        </div>
      </div>
    );
  }

  // CRITICAL: Use connection_state, NOT status for online check
  // This must match the logic in System → Geräte view
  const isOnline = device ? isDeviceOnline(device) : false;
  const devicePaired = device ? isPaired(device) : false;
  const deviceStatus = device ? normalizeDeviceStatus(device, false) : null;

  const tabs = [
    { id: 'overview', label: 'Überblick', icon: '📊' },
    { id: 'reminders', label: 'Erinnerungen', icon: '⏰', count: reminders.length },
    { id: 'emergencies', label: 'Notfälle', icon: '🚨', count: emergencies.length },
    { id: 'settings', label: 'Einstellungen', icon: '⚙️' }
  ];

  return (
    <div className="min-h-screen bg-[#050816] pb-24">
      {/* Header with Senior Info - Hayat Dark Theme */}
      <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back Button */}
          <button
            onClick={() => navigate('/family/seniors')}
            className="mb-4 flex items-center gap-2 text-[#CBD5F5] hover:text-[#6DCEE8] transition-colors font-medium"
          >
            <span className="text-xl">←</span>
            Zurück
          </button>

          {/* Senior Info */}
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {/* Avatar with Upload */}
            <div className="relative flex-shrink-0 group">
              {/* Avatar Image */}
              <SeniorAvatar 
                senior={senior} 
                size="lg" 
                className="shadow-lg"
              />
              
              {/* Upload Overlay (appears on hover) */}
              <label className="absolute inset-0 bg-black/60 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center">
                <div className="text-center">
                  {isUploadingPhoto ? (
                    <div className="text-white text-xs">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
                      Lädt...
                    </div>
                  ) : (
                    <>
                      <span className="text-white text-2xl block mb-1">📷</span>
                      <span className="text-white text-xs">Foto ändern</span>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  onChange={handlePhotoChange}
                  disabled={isUploadingPhoto}
                />
              </label>
              
              {/* Online Status Indicator */}
              {device && (
                <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-4 border-[#050816] shadow-lg flex items-center justify-center ${
                  isOnline ? 'bg-[#22C55E]' : 'bg-neutral-600'
                }`}>
                  <span className="text-white text-xs font-bold">
                    {isOnline ? '✓' : '○'}
                  </span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-[#F9FAFB] mb-2">{senior.name}</h1>
              
              {/* Photo Upload Error */}
              {photoUploadError && (
                <div className="mb-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  ❌ {photoUploadError}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-neutral-800/50 border border-neutral-600/40 rounded-xl text-sm font-medium text-[#CBD5F5]">
                  {senior.language === 'de' && '🇩🇪 Deutsch'}
                  {senior.language === 'tr' && '🇹🇷 Türkisch'}
                  {senior.language === 'ar' && '🇸🇦 Arabisch'}
                  {senior.language === 'en' && '🇬🇧 Englisch'}
                </span>
                {senior.religion && senior.religion !== 'none' && (
                  <span className="px-3 py-1 bg-[#F6B352]/10 border border-[#F6B352]/40 rounded-xl text-sm font-medium text-[#F6B352]">
                    {senior.religion === 'islam' && '☪️ Islam'}
                    {senior.religion === 'christian' && '✝️ Christlich'}
                  </span>
                )}
              </div>
            </div>

            {/* Call Buttons - Hayat Brand Colors */}
            {device && isOnline && (
              <div className="flex gap-2">
                <button
                  onClick={() => startVideoCall(false)}
                  className="px-6 py-3 bg-gradient-to-r from-[#6DCEE8] to-[#3a9dbe] text-white rounded-xl hover:shadow-xl transition-all font-semibold shadow-lg flex items-center gap-2 min-h-[44px]"
                >
                  <span className="text-xl">📹</span>
                  Videoanruf
                </button>
                <button
                  onClick={() => startVideoCall(true)}
                  className="px-4 py-3 bg-gradient-to-r from-[#F6B352] to-[#d49640] text-white rounded-xl hover:shadow-xl transition-all font-semibold shadow-lg min-h-[44px]"
                  title="Drop-In: Automatische Annahme nach 3 Sekunden"
                >
                  🚪
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs - Hayat Brand Style */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-6 py-3 rounded-xl font-semibold text-sm whitespace-nowrap
                transition-all duration-300 flex items-center gap-2 min-h-[44px]
                ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white shadow-lg scale-105'
                    : 'bg-neutral-900/80 backdrop-blur-xl text-[#CBD5F5] hover:bg-neutral-800 shadow-md border border-neutral-600/40'
                }
              `}
            >
              <span className="text-lg">{tab.icon}</span>
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === tab.id 
                    ? 'bg-white/20' 
                    : 'bg-neutral-700'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {/* ÜBERBLICK TAB */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Geräte-Status Card */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-lg border border-neutral-600/40">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6DCEE8] to-[#3a9dbe] flex items-center justify-center text-2xl shadow-lg">
                  📱
                </div>
                <h3 className="text-lg font-bold text-[#F9FAFB]">Geräte-Status</h3>
              </div>
              {device ? (
                <div className="space-y-3">
                  {/* Status Badge - Consistent with System → Geräte view */}
                  <div className="flex justify-between items-center p-3 bg-neutral-800/50 rounded-xl">
                    <span className="text-sm font-medium text-neutral-500">Status</span>
                    {deviceStatus && (
                      <span 
                        className={`text-xs px-2 py-1 rounded-full border ${deviceStatus.badge} flex items-center gap-1`}
                        title={deviceStatus.tooltip}
                      >
                        <span>{deviceStatus.emoji}</span>
                        <span className="font-semibold">{deviceStatus.label}</span>
                      </span>
                    )}
                  </div>
                  
                  {/* Inconsistency Warning */}
                  {deviceStatus && deviceStatus.isInconsistent && (
                    <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                      <p className="text-xs text-orange-300">
                        ⚠️ Dieses Gerät ist inkonsistent. Bitte im Dashboard bereinigen.
                      </p>
                    </div>
                  )}
                  {/* Device Name */}
                  <div className="flex justify-between items-center p-3 bg-neutral-800/50 rounded-xl">
                    <span className="text-sm font-medium text-neutral-500">Gerät</span>
                    <span className="text-sm font-bold text-[#F9FAFB]">
                      {device.device_name || device.name || device.serial_number || 'Unbenannt'}
                    </span>
                  </div>
                  {/* Device UUID (optional) */}
                  {device.device_uuid && (
                    <div className="flex justify-between items-center p-3 bg-neutral-800/50 rounded-xl">
                      <span className="text-sm font-medium text-neutral-500">UUID</span>
                      <span className="text-xs font-mono text-neutral-400">
                        {device.device_uuid.slice(0, 8)}...
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-neutral-500 text-sm mb-2">⚪ Kein Gerät zugewiesen</p>
                  <p className="text-xs text-neutral-600">
                    Bitte koppeln Sie ein Gerät in den Einstellungen
                  </p>
                </div>
              )}
            </div>

            {/* Nächste Erinnerung Card */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md border border-neutral-600/40">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#F6B352] to-[#d49640] flex items-center justify-center text-2xl">
                  ⏰
                </div>
                <h3 className="text-lg font-bold text-[#F9FAFB]">Nächste Erinnerung</h3>
              </div>
              {reminders.filter(r => r.status === 'pending')[0] ? (
                <div className="space-y-2">
                  <p className="font-bold text-[#F9FAFB]">
                    {reminders.filter(r => r.status === 'pending')[0].title}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {new Date(reminders.filter(r => r.status === 'pending')[0].scheduled_time).toLocaleString('de-DE')}
                  </p>
                </div>
              ) : (
                <p className="text-hayat-neutral-500 text-center py-4">Keine anstehenden Erinnerungen</p>
              )}
            </div>

            {/* Quick Settings Card */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md border border-neutral-600/40">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#5B4B8A] to-[#4a3d72] flex items-center justify-center text-2xl">
                  ⚙️
                </div>
                <h3 className="text-lg font-bold text-[#F9FAFB]">Schnelleinstellungen</h3>
              </div>
              <button
                onClick={() => navigate(`/family/seniors/${seniorId}/settings`)}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white py-3 px-4 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mb-3"
              >
                <span>✏️</span>
                Einstellungen bearbeiten
              </button>
              <button
                onClick={() => navigate(`/family/seniors/${seniorId}/device-settings`)}
                className="w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white py-3 px-4 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <span>📱</span>
                Geräte-Einstellungen
              </button>
              <div className="mt-4 space-y-2 text-sm text-neutral-400">
                <div className="flex justify-between">
                  <span>Sprache:</span>
                  <span className="font-semibold text-neutral-200">
                    {senior.preferences?.language === 'tr' ? 'Türkisch' : senior.preferences?.language === 'en' ? 'English' : 'Deutsch'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Religion:</span>
                  <span className="font-semibold text-neutral-200">
                    {senior.religion === 'islam' ? '☪️ Islam' : senior.religion === 'christian' ? '✝️ Christlich' : 'Keine'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Textgröße:</span>
                  <span className="font-semibold text-neutral-200">
                    {senior.preferences?.text_size === 'xlarge' ? 'Sehr Groß' : senior.preferences?.text_size === 'large' ? 'Groß' : 'Normal'}
                  </span>
                </div>
              </div>
            </div>

            {/* Aktivierte Module Card */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md border border-neutral-600/40">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#6DCEE8] to-[#3a9dbe] flex items-center justify-center text-2xl">
                  📦
                </div>
                <h3 className="text-lg font-bold text-[#F9FAFB]">Aktivierte Module</h3>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {senior.modules_enabled?.reminders && (
                  <span className="px-4 py-2 bg-hayat-warning-100 text-hayat-warning-700 rounded-xl text-sm font-semibold">
                    ⏰ Erinnerungen
                  </span>
                )}
                {senior.modules_enabled?.emergency && (
                  <span className="px-4 py-2 bg-hayat-danger-100 text-hayat-danger-700 rounded-xl text-sm font-semibold">
                    🚨 Notfall
                  </span>
                )}
                {senior.modules_enabled?.religion_module && (
                  <span className="px-4 py-2 bg-hayat-secondary-100 text-hayat-secondary-700 rounded-xl text-sm font-semibold">
                    ☪️ Religion
                  </span>
                )}
                {senior.modules_enabled?.calls && (
                  <span className="px-4 py-2 bg-hayat-success-100 text-[#22C55E] rounded-xl text-sm font-semibold">
                    📞 Anrufe
                  </span>
                )}
              </div>
              <button
                onClick={() => navigate(`/family/seniors/${seniorId}/modules`)}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white py-3 px-4 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <span>📦</span>
                Module verwalten
              </button>
            </div>
          </div>
        )}

        {/* ERINNERUNGEN TAB */}
        {activeTab === 'reminders' && (
          <div className="space-y-6">
            {/* Header Section */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-[#F9FAFB] mb-2">
                    Erinnerungen für {senior?.name}
                  </h2>
                  {reminders.length > 0 && (
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                      <span>📊 {reminders.length} Erinnerung{reminders.length !== 1 ? 'en' : ''}</span>
                      {reminders.filter(r => r.status === 'pending').length > 0 && (
                        <span>⏰ {reminders.filter(r => r.status === 'pending').length} ausstehend</span>
                      )}
                    </div>
                  )}
                </div>
                
                {isAdmin && (
                  <button
                    onClick={() => {
                      setEditingReminder(null);
                      setShowReminderModal(true);
                    }}
                    className="bg-[#5B4B8A] text-white px-6 py-3 rounded-2xl hover:bg-hayat-primary-600 transition-all font-semibold shadow-hayat-md hover:shadow-hayat-lg flex items-center gap-2"
                  >
                    <span className="text-xl">➕</span>
                    Neue Erinnerung
                  </button>
                )}
              </div>
              
              {!isAdmin && (
                <div className="bg-hayat-warning-50 border border-hayat-warning-200 text-hayat-warning-800 px-6 py-4 rounded-2xl flex items-start gap-3 animate-fade-in">
                  <span className="text-2xl">ℹ️</span>
                  <div>
                    <p className="font-bold mb-1">Nur-Lese-Modus</p>
                    <p className="text-sm">Sie können Erinnerungen einsehen. Nur Admins können Erinnerungen anlegen oder bearbeiten.</p>
                  </div>
                </div>
              )}
            </div>

            {/* View Toggle */}
            {reminders.length > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={() => setReminderView('list')}
                  className={`
                    px-6 py-3 rounded-2xl font-semibold text-sm
                    transition-all duration-300
                    ${
                      reminderView === 'list'
                        ? 'bg-[#5B4B8A] text-white shadow-hayat-md'
                        : 'bg-white text-[#CBD5F5] hover:bg-neutral-800/50 shadow-hayat-sm border border-hayat-neutral-200'
                    }
                  `}
                >
                  📋 Liste
                </button>
                <button
                  onClick={() => setReminderView('calendar')}
                  className={`
                    px-6 py-3 rounded-2xl font-semibold text-sm
                    transition-all duration-300
                    ${
                      reminderView === 'calendar'
                        ? 'bg-[#5B4B8A] text-white shadow-hayat-md'
                        : 'bg-white text-[#CBD5F5] hover:bg-neutral-800/50 shadow-hayat-sm border border-hayat-neutral-200'
                    }
                  `}
                >
                  📅 Kalender
                </button>
              </div>
            )}

            {/* Loading State */}
            {loadingReminders && (
              <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-12 shadow-hayat-md text-center border border-neutral-600/40">
                <div className="w-16 h-16 border-4 border-hayat-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-neutral-500">Lade Erinnerungen...</p>
              </div>
            )}

            {/* Error State */}
            {reminderError && (
              <div className="bg-hayat-danger-50 border border-hayat-danger-200 text-hayat-danger-700 px-6 py-4 rounded-2xl">
                {reminderError}
              </div>
            )}

            {/* Empty State */}
            {!loadingReminders && !reminderError && reminders.length === 0 && (
              <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-12 shadow-hayat-md text-center border border-neutral-600/40">
                <div className="text-7xl mb-4">⏰</div>
                <h3 className="text-2xl font-bold text-[#F9FAFB] mb-2">
                  Noch keine Erinnerungen angelegt
                </h3>
                <p className="text-neutral-500">
                  {isAdmin
                    ? 'Klicken Sie auf "Neue Erinnerung", um eine Erinnerung hinzuzufügen.'
                    : 'Es wurden noch keine Erinnerungen für diesen Senior angelegt.'}
                </p>
              </div>
            )}

            {/* List View */}
            {!loadingReminders && !reminderError && reminders.length > 0 && reminderView === 'list' && (
              <div className="space-y-4">
                {reminders.map(reminder => {
                  const typeConfig = {
                    medication: { icon: '💊', label: 'Medikament', gradient: 'from-blue-500 to-blue-600' },
                    appointment: { icon: '🏥', label: 'Arzttermin', gradient: 'from-purple-500 to-purple-600' },
                    routine: { icon: '📋', label: 'Routine', gradient: 'from-green-500 to-green-600' },
                    other: { icon: '📌', label: 'Sonstiges', gradient: 'from-gray-500 to-gray-600' }
                  };
                  
                  const repeatLabels = {
                    none: 'Einmalig',
                    daily: 'Täglich',
                    weekly: 'Wöchentlich',
                    monthly: 'Monatlich'
                  };

                  const config = typeConfig[reminder.reminder_type] || typeConfig.other;
                  const scheduledDate = new Date(reminder.scheduled_time);
                  const isPast = scheduledDate < new Date();

                  return (
                    <div
                      key={reminder.id}
                      className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md hover:shadow-hayat-lg transition-all border border-neutral-600/40"
                    >
                      <div className="flex gap-4">
                        {/* Icon */}
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-2xl shadow-hayat-sm flex-shrink-0`}>
                          {config.icon}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-[#F9FAFB] mb-2">
                                {reminder.title}
                              </h3>
                              <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-neutral-700 text-[#CBD5F5] rounded-xl text-xs font-semibold">
                                  {config.icon} {config.label}
                                </span>
                                {reminder.priority === 'high' && (
                                  <span className="px-3 py-1 bg-hayat-danger-100 text-hayat-danger-700 rounded-xl text-xs font-semibold">
                                    ⚠️ Wichtig
                                  </span>
                                )}
                                {reminder.repeat && reminder.repeat !== 'none' && (
                                  <span className="px-3 py-1 bg-hayat-warning-100 text-hayat-warning-700 rounded-xl text-xs font-semibold">
                                    🔁 {repeatLabels[reminder.repeat]}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Admin Actions */}
                            {isAdmin && (
                              <div className="flex gap-2 flex-shrink-0">
                                <button
                                  onClick={() => {
                                    setEditingReminder(reminder);
                                    setShowReminderModal(true);
                                  }}
                                  className="w-10 h-10 bg-neutral-700 text-[#CBD5F5] rounded-xl hover:bg-hayat-neutral-200 transition-colors flex items-center justify-center"
                                  title="Bearbeiten"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleDeleteReminder(reminder.id)}
                                  className="w-10 h-10 bg-hayat-danger-100 text-hayat-danger-700 rounded-xl hover:bg-hayat-danger-200 transition-colors flex items-center justify-center"
                                  title="Löschen"
                                >
                                  🗑️
                                </button>
                              </div>
                            )}
                          </div>

                          {reminder.description && (
                            <p className="text-neutral-500 text-sm mb-3">
                              {reminder.description.length > 150
                                ? reminder.description.substring(0, 150) + '...'
                                : reminder.description}
                            </p>
                          )}

                          <div className="flex items-center gap-4 text-sm">
                            <span className={`flex items-center gap-2 ${isPast ? 'text-hayat-neutral-400' : 'text-[#CBD5F5]'}`}>
                              <span>📅</span>
                              {scheduledDate.toLocaleDateString('de-DE', { 
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                            <span className={`flex items-center gap-2 ${isPast ? 'text-hayat-neutral-400' : 'text-[#CBD5F5]'}`}>
                              <span>🕐</span>
                              {scheduledDate.toLocaleTimeString('de-DE', { 
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Calendar View */}
            {!loadingReminders && !reminderError && reminders.length > 0 && reminderView === 'calendar' && (
              <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md border border-neutral-600/40">
                <ReminderCalendar
                  reminders={reminders}
                  onDateClick={(dateKey, dayReminders) => {
                    setSelectedDateReminders({ date: dateKey, reminders: dayReminders });
                  }}
                />
                
                {selectedDateReminders && (
                  <div className="mt-6 bg-neutral-800/50 rounded-2xl p-6 border border-hayat-neutral-200">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold text-[#F9FAFB]">
                        Erinnerungen am {new Date(selectedDateReminders.date).toLocaleDateString('de-DE', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </h3>
                      <button
                        onClick={() => setSelectedDateReminders(null)}
                        className="text-hayat-neutral-400 hover:text-neutral-500 text-2xl"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="space-y-3">
                      {selectedDateReminders.reminders.map(reminder => (
                        <div key={reminder.id} className="bg-white border-l-4 border-hayat-primary-500 rounded-xl p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-bold text-[#F9FAFB]">{reminder.title}</h4>
                              <p className="text-sm text-neutral-500">
                                {new Date(reminder.scheduled_time).toLocaleTimeString('de-DE', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            {isAdmin && (
                              <button
                                onClick={() => {
                                  setEditingReminder(reminder);
                                  setShowReminderModal(true);
                                }}
                                className="text-sm text-[#6DCEE8] hover:text-hayat-primary-800 font-semibold"
                              >
                                Bearbeiten
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reminder Modal */}
            {showReminderModal && isAdmin && (
              <ReminderFormModal
                seniorId={seniorId}
                reminder={editingReminder}
                onClose={() => {
                  setShowReminderModal(false);
                  setEditingReminder(null);
                }}
                onSaved={handleReminderSaved}
              />
            )}
          </div>
        )}

        {/* NOTFÄLLE TAB */}
        {activeTab === 'emergencies' && (
          <div>
            {emergencies.length === 0 ? (
              <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-12 shadow-hayat-md text-center border border-neutral-600/40">
                <div className="text-7xl mb-4">🚨</div>
                <h3 className="text-2xl font-bold text-[#F9FAFB] mb-2">Keine Notfälle</h3>
                <p className="text-neutral-500">Es wurden keine Notfälle aufgezeichnet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {emergencies.map(emergency => (
                  <div
                    key={emergency.id}
                    className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md hover:shadow-hayat-lg transition-all border border-hayat-danger-200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#EF4444] to-[#dc2626] flex items-center justify-center text-2xl shadow-hayat-sm flex-shrink-0">
                        🚨
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-[#F9FAFB] mb-2">
                          Notfall ausgelöst
                        </h4>
                        <p className="text-[#CBD5F5] mb-3">
                          {emergency.description || 'Keine Details'}
                        </p>
                        <p className="text-sm text-hayat-neutral-500">
                          {new Date(emergency.created_at).toLocaleString('de-DE')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* EINSTELLUNGEN TAB */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
              <h2 className="text-2xl font-bold text-[#F9FAFB] mb-2">
                Einstellungen für {senior.name}
              </h2>
              <p className="text-neutral-500 mb-4">
                Verwalten Sie die Sprache, Religion und aktivierte Module.
              </p>
              
              {!isAdmin && (
                <div className="bg-hayat-warning-50 border border-hayat-warning-200 text-hayat-warning-800 px-4 py-3 rounded-2xl flex items-start gap-3">
                  <span className="text-2xl">ℹ️</span>
                  <div>
                    <p className="font-bold">Nur-Lese-Modus</p>
                    <p className="text-sm">
                      Sie können diese Einstellungen einsehen, aber nicht ändern. Nur Admins können Änderungen vornehmen.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Success/Error Messages */}
            {settingsSuccess && (
              <div className="bg-hayat-success-50 border border-hayat-success-200 text-[#22C55E] px-6 py-4 rounded-2xl font-semibold">
                ✓ {settingsSuccess}
              </div>
            )}

            {settingsError && (
              <div className="bg-hayat-danger-50 border border-hayat-danger-200 text-hayat-danger-700 px-6 py-4 rounded-2xl font-semibold">
                ⚠️ {settingsError}
              </div>
            )}

            {/* Sprache */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
              <h3 className="text-xl font-bold text-[#F9FAFB] mb-4">Sprache</h3>
              <select
                value={settingsLanguage}
                onChange={(e) => setSettingsLanguage(e.target.value)}
                disabled={!isAdmin || settingsSaving}
                className={`
                  w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl 
                  focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent
                  text-[#F9FAFB] font-medium
                  ${!isAdmin ? 'bg-neutral-700 cursor-not-allowed' : 'bg-white'}
                `}
              >
                <option value="de">🇩🇪 Deutsch</option>
                <option value="tr">🇹🇷 Türkisch</option>
                <option value="en">🇬🇧 Englisch</option>
                <option value="ar">🇸🇦 Arabisch</option>
              </select>
            </div>

            {/* Standort */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
              <h3 className="text-xl font-bold text-[#F9FAFB] mb-4 flex items-center gap-2">
                <span>📍</span>
                Standort
              </h3>
              <p className="text-neutral-400 text-sm mb-4">
                Benötigt für Gebetszeiten, lokale Geschichten und zeitbasierte Features
              </p>
              <input
                type="text"
                value={senior.location || ''}
                onChange={(e) => setSenior({...senior, location: e.target.value})}
                disabled={!isAdmin || settingsSaving}
                placeholder="z.B. Berlin, Hamburg, München"
                className={`
                  w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl 
                  focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent
                  text-[#F9FAFB] font-medium bg-neutral-800
                  ${!isAdmin ? 'cursor-not-allowed opacity-50' : ''}
                `}
              />
            </div>

            {/* Religion */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
              <h3 className="text-xl font-bold text-[#F9FAFB] mb-4">Religion</h3>
              <select
                value={settingsReligion}
                onChange={(e) => setSettingsReligion(e.target.value)}
                disabled={!isAdmin || settingsSaving}
                className={`
                  w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl 
                  focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent
                  text-[#F9FAFB] font-medium
                  ${!isAdmin ? 'bg-neutral-700 cursor-not-allowed' : 'bg-white'}
                `}
              >
                <option value="none">Keine Angabe</option>
                <option value="islam">☪️ Islam</option>
                <option value="christian">✝️ Christentum</option>
                <option value="other">Andere</option>
              </select>
            </div>

            {/* Islamische Einstellungen */}
            {settingsReligion === 'islam' && (
              <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
                <h3 className="text-xl font-bold text-[#F9FAFB] mb-4 flex items-center gap-2">
                  <span>☪️</span>
                  Islamische Einstellungen
                </h3>
                <div className="space-y-4">
                  <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={settingsIslamic.enable_prayer_times || false}
                      onChange={(e) => setSettingsIslamic({
                        ...settingsIslamic,
                        enable_prayer_times: e.target.checked
                      })}
                      disabled={!isAdmin || settingsSaving}
                      className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                    />
                    <span className="font-medium text-[#F9FAFB]">Gebetszeiten anzeigen</span>
                  </label>

                  <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={settingsIslamic.enable_adhan || false}
                      onChange={(e) => setSettingsIslamic({
                        ...settingsIslamic,
                        enable_adhan: e.target.checked
                      })}
                      disabled={!isAdmin || settingsSaving}
                      className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                    />
                    <span className="font-medium text-[#F9FAFB]">Adhan (Gebetsruf) abspielen</span>
                  </label>

                  <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={settingsIslamic.enable_quran_verses || false}
                      onChange={(e) => setSettingsIslamic({
                        ...settingsIslamic,
                        enable_quran_verses: e.target.checked
                      })}
                      disabled={!isAdmin || settingsSaving}
                      className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                    />
                    <span className="font-medium text-[#F9FAFB]">Quran-Verse-Modul aktiv</span>
                  </label>

                  <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={settingsIslamic.enable_stories || false}
                      onChange={(e) => setSettingsIslamic({
                        ...settingsIslamic,
                        enable_stories: e.target.checked
                      })}
                      disabled={!isAdmin || settingsSaving}
                      className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                    />
                    <span className="font-medium text-[#F9FAFB]">Islamische Geschichten aktiv</span>
                  </label>

                  <div className="pt-4">
                    <label className="block text-sm font-bold text-[#F9FAFB] mb-3">
                      Berechnungsmethode für Gebetszeiten
                    </label>
                    <select
                      value={settingsIslamic.calculation_method || 'MWL'}
                      onChange={(e) => setSettingsIslamic({
                        ...settingsIslamic,
                        calculation_method: e.target.value
                      })}
                      disabled={!isAdmin || settingsSaving}
                      className={`
                        w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl 
                        focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent
                        text-[#F9FAFB] font-medium
                        ${!isAdmin ? 'bg-neutral-700 cursor-not-allowed' : 'bg-white'}
                      `}
                    >
                      <option value="MWL">Muslim World League (MWL)</option>
                      <option value="Turkey">Türkei (Diyanet)</option>
                      <option value="Makkah">Umm al-Qura (Makkah)</option>
                      <option value="Egypt">Ägypten</option>
                      <option value="ISNA">Islamic Society of North America (ISNA)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Christliche Einstellungen */}
            {settingsReligion === 'christian' && (
              <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
                <h3 className="text-xl font-bold text-[#F9FAFB] mb-4 flex items-center gap-2">
                  <span>✝️</span>
                  Christliche Einstellungen
                </h3>
                <div className="space-y-4">
                  <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={settingsChristian.enable_prayer_times || false}
                      onChange={(e) => setSettingsChristian({
                        ...settingsChristian,
                        enable_prayer_times: e.target.checked
                      })}
                      disabled={!isAdmin || settingsSaving}
                      className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                    />
                    <span className="font-medium text-[#F9FAFB]">Gebetszeiten / Gottesdienst-Zeiten anzeigen</span>
                  </label>

                  <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={settingsChristian.enable_bible_verses || false}
                      onChange={(e) => setSettingsChristian({
                        ...settingsChristian,
                        enable_bible_verses: e.target.checked
                      })}
                      disabled={!isAdmin || settingsSaving}
                      className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                    />
                    <span className="font-medium text-[#F9FAFB]">Bibelverse-Modul aktiv</span>
                  </label>

                  <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={settingsChristian.enable_stories || false}
                      onChange={(e) => setSettingsChristian({
                        ...settingsChristian,
                        enable_stories: e.target.checked
                      })}
                      disabled={!isAdmin || settingsSaving}
                      className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                    />
                    <span className="font-medium text-[#F9FAFB]">Christliche Geschichten aktiv</span>
                  </label>

                  <div className="pt-4">
                    <label className="block text-sm font-bold text-[#F9FAFB] mb-3">
                      Konfession
                    </label>
                    <select
                      value={settingsChristian.denomination || 'catholic'}
                      onChange={(e) => setSettingsChristian({
                        ...settingsChristian,
                        denomination: e.target.value
                      })}
                      disabled={!isAdmin || settingsSaving}
                      className={`
                        w-full px-4 py-3 border-2 border-hayat-neutral-200 rounded-2xl 
                        focus:outline-none focus:ring-2 focus:ring-hayat-primary-500 focus:border-transparent
                        text-[#F9FAFB] font-medium
                        ${!isAdmin ? 'bg-neutral-700 cursor-not-allowed' : 'bg-white'}
                      `}
                    >
                      <option value="catholic">Katholisch</option>
                      <option value="protestant">Evangelisch</option>
                      <option value="orthodox">Orthodox</option>
                      <option value="other">Andere</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Module */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
              <h3 className="text-xl font-bold text-[#F9FAFB] mb-2">
                Module aktivieren/deaktivieren
              </h3>
              <p className="text-sm text-neutral-500 mb-4">
                Bestimmen Sie, welche Funktionen auf dem Gerät verfügbar sein sollen.
              </p>
              <div className="space-y-4">
                <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={settingsModules.reminders || false}
                    onChange={(e) => setSettingsModules({
                      ...settingsModules,
                      reminders: e.target.checked
                    })}
                    disabled={!isAdmin || settingsSaving}
                    className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                  />
                  <span className="font-medium text-[#F9FAFB]">⏰ Erinnerungen</span>
                </label>

                <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={settingsModules.calls || false}
                    onChange={(e) => setSettingsModules({
                      ...settingsModules,
                      calls: e.target.checked
                    })}
                    disabled={!isAdmin || settingsSaving}
                    className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                  />
                  <span className="font-medium text-[#F9FAFB]">📞 Familienanrufe</span>
                </label>

                <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={settingsModules.religion_module || false}
                    onChange={(e) => setSettingsModules({
                      ...settingsModules,
                      religion_module: e.target.checked
                    })}
                    disabled={!isAdmin || settingsSaving}
                    className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                  />
                  <span className="font-medium text-[#F9FAFB]">☪️ Religion-Kachel</span>
                </label>

                <label className="flex items-center gap-3 p-4 bg-neutral-800/50 rounded-xl cursor-pointer hover:bg-neutral-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={settingsModules.emergency || false}
                    onChange={(e) => setSettingsModules({
                      ...settingsModules,
                      emergency: e.target.checked
                    })}
                    disabled={!isAdmin || settingsSaving}
                    className="w-5 h-5 text-[#5B4B8A] bg-white border-hayat-neutral-300 rounded focus:ring-hayat-primary-500 focus:ring-2 disabled:opacity-50"
                  />
                  <span className="font-medium text-[#F9FAFB]">🚨 Notfall-Kachel</span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={handleSaveSettings}
                disabled={!isAdmin || settingsSaving}
                className={`
                  flex-1 py-4 px-6 rounded-2xl font-bold text-white
                  transition-all shadow-hayat-md hover:shadow-hayat-lg
                  ${
                    isAdmin && !settingsSaving
                      ? 'bg-[#5B4B8A] hover:bg-hayat-primary-600'
                      : 'bg-hayat-neutral-300 cursor-not-allowed'
                  }
                `}
              >
                {settingsSaving ? 'Wird gespeichert...' : '💾 Einstellungen speichern'}
              </button>
              <button
                onClick={handleResetSettings}
                disabled={!isAdmin || settingsSaving}
                className={`
                  flex-1 py-4 px-6 rounded-2xl font-bold
                  transition-all shadow-hayat-sm hover:shadow-hayat-md
                  ${
                    isAdmin && !settingsSaving
                      ? 'bg-white text-[#CBD5F5] hover:bg-neutral-800/50 border-2 border-hayat-neutral-200'
                      : 'bg-neutral-700 text-hayat-neutral-400 cursor-not-allowed'
                  }
                `}
              >
                🔄 Änderungen verwerfen
              </button>
            </div>

            {!isAdmin && (
              <div className="bg-hayat-warning-50 border border-hayat-warning-200 text-hayat-warning-700 px-6 py-4 rounded-2xl flex items-center gap-3">
                <span className="text-2xl">⚠️</span>
                <p className="font-medium">
                  Nur Admins können diese Einstellungen ändern. Bitte wenden Sie sich an den Administrator der Familie.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FamilySeniorDetail;
